import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from '@puppycad/proto'
import { logger } from '@/utils'
import { downloadCadFile } from '@/services/api/cad-session'
import {
  analyzeImportedModel,
  type AssemblyManifest,
  type AssemblyManifestCache,
  type AssemblyManifestLoadResult,
  type ImportedAssemblyReferenceResolver,
  type AssemblyModelCache,
  applyGltfBodyEntriesToScene,
  composeAssemblyModel,
  createCachedAssemblyResult,
  createEmbeddedAssemblyModelAsset,
  createInlineAssemblyModelAsset,
  createReferencedAssemblyModelAsset,
  disposeAssemblyModelAssetResources,
  disposePartAsset,
  filterGlbFiles,
  isImportedBrepHydrationError,
  toImportedBrepHydrationError,
  type ParsedGlbCache,
  parseAssemblyManifestGltf,
  parseGlb,
  parseInlinePartAsset,
  parsePartAssetGltf,
  planImportedModelLoad,
  type BRepRegistrar,
  type ComposedAssemblyResult,
  type PartAssetCache,
  type ResolvedAssemblyAssets,
} from '@puppycad/cad-core'
import {
  isImportedModelLoadAbortedError,
  throwIfImportedModelRequestStale,
  type ImportedModelLoadGuard,
} from './imported-model-load-guard'
import { ImportedModelLoadTransaction } from './imported-model-load-transaction'
import { ImportedModelRequestRegistry } from './imported-model-request-registry'
import { buildParsedGlbArtifactKey } from './imported-model-artifact-key'
import {
  createExternalReferencedSourceDescriptor,
  createProjectReferencedSourceDescriptor,
  normalizeReferenceFileId,
  normalizeResolvedReferenceFile,
  resolveReferenceFileIdAliases,
  type ReferencedSourceDescriptor,
} from './assembly-reference/assembly-reference-source'
import {
  hydrateImportedAssemblyAssetBrepRuntime,
  hydrateImportedPartAssetBrepRuntime,
} from './imported-model-brep-loader'
import type { ImportedBrepRuntimeCache } from './imported-model-brep-runtime-cache'
import type { ImportedModelFileBytesReader } from './imported-model-loader'
import { mapWithConcurrencyLimit } from './map-with-concurrency-limit'

type LoadAssemblyModelOptions = {
  projectFiles: FileInfo[]
  version: string
  partAssetCache: PartAssetCache
  assemblyManifestCache: AssemblyManifestCache
  assemblyModelCache: AssemblyModelCache
  parsedGlbCache: ParsedGlbCache
  importedBrepRuntimeCache?: ImportedBrepRuntimeCache
  brepRegistrar?: BRepRegistrar
  sourceGltf?: GLTF
  manifestResult?: Pick<AssemblyManifestLoadResult, 'manifest' | 'warnings' | 'inlinePartSourceByNodeId' | 'inlineAssemblySourceByNodeId'>
  guard?: ImportedModelLoadGuard
  lineage?: string[]
  projectId?: string
  selectedFileId?: string
  referenceResolver?: ImportedAssemblyReferenceResolver
  /** 可选文件读取器；轻量 ZIP 节点包通过它从内存 entry 读取 GLB 和 sidecar。 */
  readFileBytes?: ImportedModelFileBytesReader
  /**
   * 语义资产 staging 的目标事务。所有 `partAssetCache` / `assemblyModelCache` 的
   * 写入都必须通过它，禁止在 loader 内部直接 `.set()`。commit / abort / rollback
   * 由 caller 按生命周期统一处理。
   */
  transaction: ImportedModelLoadTransaction
  /**
   * 宿主共享的 request registry。与 `imported-model-loader.ts` 相同——用同一
   * `parsed-glb:<uuid>:<version>` 格式的 artifactKey 跨两处 loader 共享 in-flight
   * 下载 promise，避免抢占期间的重复 GLB 下载（Fix B）。
   */
  requestRegistry?: ImportedModelRequestRegistry
  /**
   * child-load 可视化回调。在 `resolveReferencedChild` 里经过 lineage / self-ref /
   * cache-hit 短路之后、真正要触发 download + parse 之前 fire `start`；try/finally
   * 保证 `complete` 在正常与异常路径下都一定对应被触发。递归进入嵌套装配体加载时
   * 会随 `...options` spread 自动传递，因此深层孙级引用也能反映到 UI。
   *
   * `complete` 的 `outcome` 语义：
   *   - `'staged'`：子资产已 stage 到当前事务（尚未 commit）；宿主可以延后 spinner
   *     清零到 cache subscription fire，从而把 icon 回填与 spinner 清零耦合到同一帧；
   *   - `'aborted'`：事务已 aborted（例如协作抢占、显式 abort、抛错冒泡）；宿主应当
   *     立即清零 spinner（不会再有 cache 写入来触发 subscription）；
   *   - `'missing'`：子引用无法解析（`result.missing = true`），同 aborted 需要
   *     立即清零 spinner。
   */
  onChildResolveStart?: (uuid: string) => void
  onChildResolveComplete?: (uuid: string, outcome: 'staged' | 'aborted' | 'missing') => void
}

/**
 * 同一父装配下解析外部引用子项的最大并发数。
 *
 * 设计说明：
 * 1. 旧实现直接 `Promise.all(referencedPartUuids.map(...))`，大装配会同时拉起大量下载/解析；
 * 2. 这里保留“同层并发”以维持吞吐，但给出固定上限，避免一次性占满网络、内存与 GLTF 解析资源；
 * 3. 顺序确定性仍由后续合并阶段保证，因此这里只控制 in-flight 数量，不改变最终输出顺序。
 */
const MAX_REFERENCED_CHILD_RESOLVE_CONCURRENCY = 4
const EXTERNAL_REFERENCE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu

function releaseParserJson(gltf: GLTF | undefined): void {
  const parser = gltf?.parser as { json?: unknown } | undefined
  if (parser) {
    parser.json = undefined
  }
}

function resolveReferencedFileSemanticHint(
  manifest: AssemblyManifest,
  fileUuid: string,
): 'assembly' | 'part' | null {
  let hasPartReference = false

  for (const node of Object.values(manifest.nodesById)) {
    if (node.partFileUuid !== fileUuid) {
      continue
    }
    if (node.semanticKind === 'assembly') {
      return 'assembly'
    }
    if (node.semanticKind === 'part') {
      hasPartReference = true
    }
  }

  return hasPartReference ? 'part' : null
}

function resolveReferencedFileIdHint(
  manifest: AssemblyManifest,
  fileUuid: string,
): string | undefined {
  const mappedFileId = manifest.externalReferenceFileIdByUuid?.[fileUuid]?.trim()
  if (mappedFileId) {
    return mappedFileId
  }
  return undefined
}

/**
 * 按后端外部引用规则生成 GetFile.file_id。
 *
 * 设计说明：
 * 1. 后端要求外部引用下载时使用 `projectId/external_file_id`；
 * 2. projectId 可能带尾部 `/`，external_file_id 可能带头部 `/`，这里统一裁剪后只保留一个分隔符；
 * 3. 当 projectId 缺失时保留 external_file_id 原值，兼容插入零部件等没有项目上下文的解析器路径。
 *
 * @param projectId 当前项目 ID。
 * @param externalFileId manifest 节点中的 external_file_id。
 * @returns 最终传给 GetFile.file_id 的文件 ID。
 */
function resolveExternalReferenceDownloadFileId(
  projectId: string | undefined,
  externalFileId: string | undefined,
): string | undefined {
  const normalizedExternalFileId = normalizeReferenceFileId(externalFileId)
  if (!normalizedExternalFileId) {
    return undefined
  }

  const normalizedProjectId = normalizeReferenceFileId(projectId)
  if (!normalizedProjectId) {
    return normalizedExternalFileId
  }

  if (
    normalizedExternalFileId === normalizedProjectId
    || normalizedExternalFileId.startsWith(`${normalizedProjectId}/`)
  ) {
    return normalizedExternalFileId
  }

  return `${normalizedProjectId}/${normalizedExternalFileId.replace(/^\/+/u, '')}`
}

/**
 * 拼接两个已经归一化语义的文件 ID 片段。
 *
 * 设计说明：
 * 1. 外部引用可能已经包含项目根路径，不能再次前缀化；
 * 2. 当前函数只负责路径候选生成，不承担下载决策，避免把 projectFiles 查询逻辑和 GetFile 入参耦合。
 *
 * @param prefix 父级目录或项目根。
 * @param child 外部引用路径。
 * @returns 可用于项目文件精确匹配的候选路径。
 */
function joinReferenceFileId(prefix: string | undefined, child: string | undefined): string | undefined {
  const normalizedChild = normalizeReferenceFileId(child)
  if (!normalizedChild) {
    return undefined
  }

  const normalizedPrefix = normalizeReferenceFileId(prefix)
  if (!normalizedPrefix) {
    return normalizedChild
  }

  if (
    normalizedChild === normalizedPrefix
    || normalizedChild.startsWith(`${normalizedPrefix}/`)
  ) {
    return normalizedChild
  }

  return `${normalizedPrefix}/${normalizedChild.replace(/^\/+/u, '')}`
}

/**
 * 生成当前 GLB 所在目录到项目根的祖先目录。
 *
 * 设计说明：
 * 1. STEP 导入会把真实零件 GLB 放在 `项目/STEP容器/导出装配/零件.glb`；
 * 2. manifest 中的 `external_file_id` 常常只保存 `导出装配/零件.glb`；
 * 3. 因此项目文件查找必须尝试当前源 GLB 的各级父目录，才能找回带 STEP 容器层的真实文件。
 *
 * @param fileId 当前正在解析的 GLB 文件 ID。
 * @returns 从近到远排序的父级目录候选。
 */
function resolveReferenceFileAncestorDirectories(fileId: string | undefined): string[] {
  const normalizedFileId = normalizeReferenceFileId(fileId)
  if (!normalizedFileId) {
    return []
  }

  const segments = normalizedFileId.split('/').filter((segment) => segment.length > 0)
  if (segments.length <= 1) {
    return []
  }

  const directorySegments = segments.slice(0, -1)
  return directorySegments
    .map((_, index) => directorySegments.slice(0, directorySegments.length - index).join('/'))
    .filter((candidate) => candidate.length > 0)
}

/**
 * 生成外部引用可命中的项目文件 ID 候选。
 *
 * 设计说明：
 * 1. 项目文件列表是最可靠的本地事实，查找时应优先穷尽项目内真实路径；
 * 2. GetFile 的默认 hint 仍保留 `projectId/external_file_id`，但它不应抢在真实项目文件之前；
 * 3. 返回值会去重，保证同一个候选不会重复参与 Map 查询。
 *
 * @param input 候选生成所需的上下文。
 * @returns 可用于 `fileByFileId` 精确查找的候选路径列表。
 */
function resolveReferencedProjectFileIdCandidates(input: {
  rawReferenceFileId: string | undefined
  downloadFileIdHint: string | undefined
  sourceFileId: string
  projectId: string | undefined
}): string[] {
  const rawReferenceFileId = normalizeReferenceFileId(input.rawReferenceFileId)
  const candidates = [
    rawReferenceFileId,
    ...resolveReferenceFileAncestorDirectories(input.sourceFileId)
      .map((directory) => joinReferenceFileId(directory, rawReferenceFileId)),
    input.downloadFileIdHint,
    joinReferenceFileId(input.projectId, rawReferenceFileId),
  ]

  return Array.from(new Set(
    candidates
      .map((candidate) => normalizeReferenceFileId(candidate))
      .filter((candidate) => candidate.length > 0),
  ))
}

/**
 * 按项目文件 ID 或导出 external_file_id hint 查找 GLB 文件。
 *
 * @param fileId 原始项目文件 ID 或导出 external_file_id。
 * @param fileByFileId normalized fileId 到 GLB 文件的映射。
 * @returns 命中的项目 GLB 文件。
 */
function resolveGlbFileByReferenceFileId(
  fileId: string | undefined,
  fileByFileId: ReadonlyMap<string, FileInfo>,
): FileInfo | undefined {
  for (const candidate of resolveReferenceFileIdAliases(fileId)) {
    const file = fileByFileId.get(candidate)
    if (file) {
      return file
    }
  }
  return undefined
}

/**
 * 按路径尾部唯一匹配项目 GLB 文件。
 *
 * 设计说明：
 * 1. 这是精确候选全部失败后的兜底，只在唯一命中时生效；
 * 2. 多个同名零件出现在不同目录时直接放弃，避免错误加载另一实例的几何。
 *
 * @param fileId 外部引用路径。
 * @param fileByFileId normalized fileId 到 GLB 文件的映射。
 * @returns 唯一命中的项目 GLB 文件。
 */
function resolveUniqueGlbFileByReferenceSuffix(
  fileId: string | undefined,
  fileByFileId: ReadonlyMap<string, FileInfo>,
): FileInfo | undefined {
  const aliases = resolveReferenceFileIdAliases(fileId)
  if (aliases.length === 0) {
    return undefined
  }

  const matches = new Map<string, FileInfo>()
  for (const [candidateFileId, file] of fileByFileId.entries()) {
    if (aliases.some((alias) => (
      candidateFileId === alias
      || candidateFileId.endsWith(`/${alias}`)
    ))) {
      matches.set(`${file.uuid}:${file.fileId}`, file)
    }
  }

  return matches.size === 1 ? Array.from(matches.values())[0] : undefined
}

/**
 * 判断外部引用的 fileId 是否实际上指向当前正在加载的文件。
 *
 * 设计说明：
 * 1. 多层导入时，当前 tab 的 fileId 可能带有父级容器前缀，例如
 *    `Assembly2.assembly.cad/Assembly2.stp/Assembly1/Part1.glb`；
 * 2. GLB 节点 extras.external_file_id 可能只记录导出器内部相对路径，例如
 *    `/Assembly1/Part1.glb|uuid`；
 * 3. 这两者不是字符串全等，但后者是前者的路径尾部，语义上表示当前文件自引用，应跳过下载。
 *
 * @param currentFileId 当前正在加载的文件 ID。
 * @param referenceFileId 外部引用携带的 fileId 或 external_file_id。
 * @returns 是否应按当前文件自引用处理。
 */
function isCurrentFileReferenceFileId(
  currentFileId: string,
  referenceFileId: string | undefined,
): boolean {
  const normalizedCurrentFileId = normalizeReferenceFileId(currentFileId)
  if (!normalizedCurrentFileId) {
    return false
  }

  return resolveReferenceFileIdAliases(referenceFileId).some((candidate) => (
    candidate === normalizedCurrentFileId
    || normalizedCurrentFileId.endsWith(`/${candidate}`)
    || candidate.endsWith(`/${normalizedCurrentFileId}`)
  ))
}

/**
 * 构建 GLB 文件的 fileId 索引。
 *
 * @param glbFiles 当前项目中可加载的 GLB 文件。
 * @returns normalized fileId 到 FileInfo 的映射。
 */
function buildGlbFileByFileId(glbFiles: FileInfo[]): Map<string, FileInfo> {
  const fileByFileId = new Map<string, FileInfo>()
  glbFiles.forEach((file) => {
    resolveReferenceFileIdAliases(file.fileId).forEach((normalizedFileId) => {
      if (normalizedFileId.length > 0 && !fileByFileId.has(normalizedFileId)) {
        fileByFileId.set(normalizedFileId, file)
      }
    })
  })
  return fileByFileId
}

/**
 * 判断外部引用是否可以直接按 external_file_uuid 请求 GetFile。
 *
 * 设计说明：
 * 1. 后端真实 UUID 缺少 external_file_id 时，允许 `fileId='' + fileUuid` 请求；
 * 2. 路径型引用 key 可能来自缺失 external_file_uuid 的旧导出数据，不能被当作 UUID 猜测下载；
 * 3. 插入链路提供 referenceResolver 时，路径型引用交给宿主按当前 GLB fileId 补齐。
 */
function shouldCreateDirectExternalSourceDescriptor(input: {
  partUuid: string
  partFileIdHint: string | undefined
  projectPartFile: FileInfo | undefined
  hasReferenceResolver: boolean
}): boolean {
  const normalizedPartUuid = normalizeReferenceFileId(input.partUuid)
  if (!normalizedPartUuid) {
    return false
  }

  if (input.partFileIdHint || input.projectPartFile || input.hasReferenceResolver) {
    return true
  }

  return EXTERNAL_REFERENCE_UUID_RE.test(normalizedPartUuid)
}

/**
 * 解析装配外部引用对应的项目 GLB 文件。
 *
 * 设计说明：
 * 1. external_file_uuid 只负责判断“这是一个外部引用节点”以及作为缓存 key；
 * 2. 外部引用真正传给 GetFile.file_id 的值只能来自 `projectId/external_file_id`；
 * 3. 因此这里只按拼接后的 explicit external_file_id 查找项目文件，不再把 uuid 或引用 key 当 fileId 猜测。
 *
 * @param partFileIdHint 已按项目 ID 拼接后的 external_file_id。
 * @param fileByFileId normalized fileId 到 GLB 文件的映射。
 * @returns 命中的项目 GLB 文件。
 */
function resolveReferencedProjectGlbFile(
  partUuid: string,
  projectFileIdCandidates: readonly string[],
  rawPartFileIdHint: string | undefined,
  fileByUuid: ReadonlyMap<string, FileInfo>,
  fileByFileId: ReadonlyMap<string, FileInfo>,
): FileInfo | undefined {
  const matchedByUuid = fileByUuid.get(partUuid)
  if (matchedByUuid) {
    return matchedByUuid
  }

  for (const candidate of projectFileIdCandidates) {
    const matchedByCandidate = resolveGlbFileByReferenceFileId(candidate, fileByFileId)
    if (matchedByCandidate) {
      return matchedByCandidate
    }
  }

  return resolveUniqueGlbFileByReferenceSuffix(rawPartFileIdHint, fileByFileId)
}

/**
 * 构建当前引用可能命中的缓存 key。
 *
 * @param partUuid manifest 引用 key。
 * @param partFile 已解析到的项目文件。
 * @returns 去重后的缓存 key 列表。
 */
function resolveReferencedCacheKeys(partUuid: string, partFile: FileInfo | undefined): string[] {
  return Array.from(new Set(
    [partUuid, partFile?.uuid].filter((key): key is string => Boolean(key?.trim())),
  ))
}

async function parseResolvedReferenceGltf(
  file: FileInfo,
  bytes: Uint8Array,
  options: Pick<
    LoadAssemblyModelOptions,
    'version' | 'parsedGlbCache' | 'guard' | 'transaction'
  >,
): Promise<GLTF> {
  const cached = options.parsedGlbCache.get(file.uuid, options.version)
  if (cached) {
    throwIfImportedModelRequestStale(options.guard)
    options.transaction?.trackParsedArtifact(file.uuid, options.version)
    return cached
  }

  const loader = new GLTFLoader()
  throwIfImportedModelRequestStale(options.guard)
  const gltf = await parseGlb(loader, bytes)
  throwIfImportedModelRequestStale(options.guard)
  // 事务被 abort 后，晚到的解析结果不能重新污染 ParsedGlbCache。
  if (!options.transaction.isAborted) {
    options.parsedGlbCache.set(file.uuid, options.version, gltf)
  }
  options.transaction?.trackParsedArtifact(file.uuid, options.version)
  return gltf
}


async function downloadReferencedSourceGltf(
  sessionId: string,
  source: ReferencedSourceDescriptor,
  options: Pick<
    LoadAssemblyModelOptions,
    | 'version'
    | 'parsedGlbCache'
    | 'guard'
    | 'transaction'
    | 'requestRegistry'
    | 'readFileBytes'
  >,
): Promise<GLTF> {
  const { assetFile, downloadTarget } = source
  const cached = options.parsedGlbCache.get(assetFile.uuid, options.version)
  if (cached) {
    throwIfImportedModelRequestStale(options.guard)
    options.transaction?.trackParsedArtifact(assetFile.uuid, options.version)
    return cached
  }

  const registry = options.requestRegistry
  const artifactKey = buildParsedGlbArtifactKey(assetFile.uuid, options.version)
  let downloadPromise: Promise<GLTF> | undefined = registry?.getInFlightArtifact<GLTF>(artifactKey)
  if (!downloadPromise) {
    downloadPromise = (async () => {
      const loader = new GLTFLoader()
      const bytes = options.readFileBytes
        ? await options.readFileBytes({
            ...assetFile,
            fileId: downloadTarget.fileId || assetFile.fileId,
            uuid: downloadTarget.fileUuid || assetFile.uuid,
          })
        : await downloadCadFile(
            sessionId,
            downloadTarget.fileId,
            downloadTarget.fileUuid,
          )
      const gltf = await parseGlb(loader, bytes)
      // 事务被 abort 后，晚到的 in-flight artifact 不能重新写回共享 cache。
      if (!options.transaction.isAborted) {
        options.parsedGlbCache.set(assetFile.uuid, options.version, gltf)
      }
      return gltf
    })()
    if (registry) {
      registry.registerInFlightArtifact(artifactKey, downloadPromise)
    }
  }

  throwIfImportedModelRequestStale(options.guard)
  const gltf = await downloadPromise
  throwIfImportedModelRequestStale(options.guard)
  options.transaction?.trackParsedArtifact(assetFile.uuid, options.version)
  return gltf
}

async function loadResolvedAssemblyAssets(
  sessionId: string,
  file: FileInfo,
  options: LoadAssemblyModelOptions,
): Promise<ResolvedAssemblyAssets | null> {
  let manifestResult = options.manifestResult ?? options.assemblyManifestCache.get(file.uuid, options.version)
  /** 无 external_file_uuid 但含内嵌几何的节点源 Object3D，用于生成内联 PartAsset */
  let inlinePartSourceByNodeId: Record<string, THREE.Object3D> = {}
  /** 无 external_file_uuid 且语义为 assembly 的节点源 Object3D，用于生成内联 AssemblyModelAsset */
  let inlineAssemblySourceByNodeId: Record<string, THREE.Object3D> = {}
  if (!manifestResult) {
    const manifestGltf = options.sourceGltf
      ? options.sourceGltf
      : await downloadReferencedSourceGltf(
          sessionId,
          createProjectReferencedSourceDescriptor(file),
          options,
        )
    throwIfImportedModelRequestStale(options.guard)
    const parsedManifest = parseAssemblyManifestGltf(manifestGltf, file, options.version)
    manifestResult = {
      manifest: parsedManifest.manifest,
      warnings: parsedManifest.warnings,
      inlinePartSourceByNodeId: parsedManifest.inlinePartSourceByNodeId ?? {},
      inlineAssemblySourceByNodeId: parsedManifest.inlineAssemblySourceByNodeId ?? {},
    }
    inlinePartSourceByNodeId = parsedManifest.inlinePartSourceByNodeId ?? {}
    inlineAssemblySourceByNodeId = parsedManifest.inlineAssemblySourceByNodeId ?? {}
    options.assemblyManifestCache.set(file.uuid, options.version, manifestResult)
  } else {
    // manifestResult 来自缓存或 options.manifestResult
    if ('inlinePartSourceByNodeId' in manifestResult && manifestResult.inlinePartSourceByNodeId) {
      inlinePartSourceByNodeId = manifestResult.inlinePartSourceByNodeId as Record<string, THREE.Object3D>
    }
    if ('inlineAssemblySourceByNodeId' in manifestResult && manifestResult.inlineAssemblySourceByNodeId) {
      inlineAssemblySourceByNodeId = manifestResult.inlineAssemblySourceByNodeId as Record<string, THREE.Object3D>
    }
    throwIfImportedModelRequestStale(options.guard)
  }

  const partAssetsByUuid: ResolvedAssemblyAssets['partAssetsByUuid'] = {}
  const assemblyAssetsByUuid: ResolvedAssemblyAssets['assemblyAssetsByUuid'] = {}
  const missingPartUuids: string[] = []
  const warnings = [...manifestResult.warnings]
  const glbFiles = filterGlbFiles(options.projectFiles)
  const fileByUuid = new Map(glbFiles.map((item) => [item.uuid, item]))
  const fileByFileId = buildGlbFileByFileId(glbFiles)
  const lineage = [...(options.lineage ?? []), file.uuid]
  const selectedFileId = options.selectedFileId ?? file.fileId

  /**
   * 单个引用子项的并行解析结果。
   *
   * 设计要点：
   * - 所有副作用（stagePartAsset / stageAssemblyAsset / parsedGlbCache.delete 等）在
   *   各并行任务内部直接完成——这些操作都是同步 + 幂等的（key 互斥，tx staging 只是 push）。
   * - warnings / missing 通过结果对象回流，由 Promise.all 完成后串行合并，保持与
   *   referencedPartUuids 原顺序一致的确定性输出。
   */
  type ChildResolution = {
    partUuid: string
    partAsset?: ResolvedAssemblyAssets['partAssetsByUuid'][string]
    assemblyAsset?: ResolvedAssemblyAssets['assemblyAssetsByUuid'][string]
    warnings: string[]
    missing: boolean
    skip: boolean
  }

  const resolveReferencedChild = async (partUuid: string): Promise<ChildResolution> => {
    const result: ChildResolution = {
      partUuid,
      warnings: [],
      missing: false,
      skip: false,
    }
    throwIfImportedModelRequestStale(options.guard)

    const semanticHint = resolveReferencedFileSemanticHint(manifestResult.manifest, partUuid)
    const rawPartFileIdHint = resolveReferencedFileIdHint(manifestResult.manifest, partUuid)
    const partFileIdHint = resolveExternalReferenceDownloadFileId(options.projectId, rawPartFileIdHint)
    const projectFileIdCandidates = resolveReferencedProjectFileIdCandidates({
      rawReferenceFileId: rawPartFileIdHint,
      downloadFileIdHint: partFileIdHint,
      sourceFileId: file.fileId,
      projectId: options.projectId,
    })
    const hasExternalFileUuidIdentity =
      normalizeReferenceFileId(partUuid).length > 0
      && normalizeReferenceFileId(partUuid) !== normalizeReferenceFileId(partFileIdHint)
    const projectPartFile = hasExternalFileUuidIdentity
      ? resolveReferencedProjectGlbFile(
          partUuid,
          projectFileIdCandidates,
          rawPartFileIdHint,
          fileByUuid,
          fileByFileId,
        )
      : undefined
    const effectivePartFileIdHint = projectPartFile?.fileId ?? partFileIdHint
    const shouldUseDirectExternalSource =
      hasExternalFileUuidIdentity
      && shouldCreateDirectExternalSourceDescriptor({
        partUuid,
        partFileIdHint: effectivePartFileIdHint,
        projectPartFile,
        hasReferenceResolver: Boolean(options.referenceResolver),
      })
    const directExternalSource = shouldUseDirectExternalSource
      ? createExternalReferencedSourceDescriptor(
          partUuid,
          effectivePartFileIdHint,
          projectPartFile,
        )
      : undefined
    const referencedSource = directExternalSource
      ?? (projectPartFile ? createProjectReferencedSourceDescriptor(projectPartFile) : undefined)
    const hintedPartFile = referencedSource?.assetFile
    // 插入/外部引用链路可能只给当前 GLB fileId，子引用的路径需要宿主 resolver 结合当前 GLB 路径补齐；
    // 因此当通用 external source 没有 fileId 时，不能提前绕过 referenceResolver。
    const shouldDelegateEmptyFileIdSourceToReferenceResolver = Boolean(
      options.referenceResolver
      && referencedSource
      && normalizeReferenceFileId(referencedSource.assetFile.fileId).length === 0,
    )
    const referencedCacheKeys = resolveReferencedCacheKeys(partUuid, hintedPartFile)
    const callbackUuid = hintedPartFile?.uuid ?? partUuid
    const isSelfReference =
      partUuid === file.uuid
      || hintedPartFile?.uuid === file.uuid
      || isCurrentFileReferenceFileId(file.fileId, partFileIdHint)
      || isCurrentFileReferenceFileId(file.fileId, rawPartFileIdHint)
      || isCurrentFileReferenceFileId(file.fileId, hintedPartFile?.fileId)
      || isCurrentFileReferenceFileId(file.fileId, partUuid)

    if (isSelfReference) {
      result.warnings.push(`忽略装配文件自引用: ${partUuid}`)
      result.skip = true
      return result
    }

    const isCircularReference =
      lineage.includes(partUuid)
      || (hintedPartFile ? lineage.includes(hintedPartFile.uuid) : false)

    if (isCircularReference) {
      result.warnings.push(`忽略循环装配引用: ${file.uuid} -> ${partUuid}`)
      result.skip = true
      return result
    }

    const cachedAssembly = referencedCacheKeys
      .map((cacheKey) => options.assemblyModelCache.get(
        cacheKey,
        options.version,
        options.transaction.publishScopeKey,
      ))
      .find((asset) => Boolean(asset))
    if (cachedAssembly) {
      result.assemblyAsset = cachedAssembly
      return result
    }

    const cachedPart = semanticHint === 'assembly'
      ? undefined
      : referencedCacheKeys
          .map((cacheKey) => options.partAssetCache.get(
            cacheKey,
            options.version,
            options.transaction.publishScopeKey,
          ))
          .find((asset) => Boolean(asset))
    if (cachedPart) {
      result.partAsset = cachedPart
      return result
    }

    // 过了所有 cache-hit 短路，接下来必然触发下载/解析（或 missing 兜底）。
    // 这里是 spinner-worthy 的起点；try/finally 保证即便嵌套 loadAssemblyModel /
    // 下载 / stale guard 抛错，complete 回调也一定会 fire。
    options.onChildResolveStart?.(callbackUuid)
    // 本任务要报告给宿主的 outcome。默认 'staged'——正常路径走到 finally 时，
    // 如果事务没 aborted 且非 missing，则表示子资产确实已 stage 进事务。
    // - catch 块进入时重置为 'aborted'（throw 会 propagate，外层 Promise.all / caller
    //   通常会 abort 事务；即便 caller 没显式 abort，这里标 'aborted' 更保守，宿主据此
    //   立即清 spinner，不会卡在等 subscription）。
    // - finally 再做一次最终校正：如果事务已 aborted（catch 没覆盖到、但其它任务抛错
    //   导致 caller abort 或协作抢占等），以事务状态为准。
    let reportedOutcome: 'staged' | 'aborted' | 'missing' = 'staged'
    let partFile = hintedPartFile
    try {
      let referencedGltf: GLTF | null = null

      if (referencedSource && !shouldDelegateEmptyFileIdSourceToReferenceResolver) {
        referencedGltf = await downloadReferencedSourceGltf(sessionId, referencedSource, options)
      } else if (options.referenceResolver) {
        const resolvedReference = await options.referenceResolver.loadReference({
          sessionId,
          selectedFileId,
          sourceFileId: file.fileId,
          externalFileUuid: partUuid,
          externalFileId: effectivePartFileIdHint,
        })
        if (resolvedReference) {
          partFile = normalizeResolvedReferenceFile(
            resolvedReference.file,
            partUuid,
            effectivePartFileIdHint,
          )
          referencedGltf = await parseResolvedReferenceGltf(
            partFile,
            resolvedReference.bytes,
            options,
          )
        }
      }

      if (!partFile || !referencedGltf) {
        result.missing = true
        reportedOutcome = 'missing'
        result.warnings.push(`缺少外部零件文件: ${partUuid}`)
        return result
      }
      throwIfImportedModelRequestStale(options.guard)

      // 当节点语义为 assembly 时，才分析是否包含子装配体清单；
      // 其余情况（part 或未知）直接按零件解析，避免不必要的分析调用
      if (semanticHint === 'assembly') {
        const analysis = analyzeImportedModel(referencedGltf, partFile, options.version)
        const plan = planImportedModelLoad(analysis)
        const shouldPreferAssemblyStructure =
          plan.loadStrategy === 'assembly-structure'
          || (semanticHint === 'assembly' && analysis.hasAssemblyManifest)

        if (shouldPreferAssemblyStructure) {
          const nestedManifest = parseAssemblyManifestGltf(referencedGltf, partFile, options.version)
          const nestedAssembly = await loadAssemblyModel(sessionId, partFile, {
            ...options,
            sourceGltf: referencedGltf,
            manifestResult: {
              manifest: nestedManifest.manifest,
              warnings: [...analysis.warnings, ...nestedManifest.warnings],
              inlinePartSourceByNodeId: nestedManifest.inlinePartSourceByNodeId ?? {},
              inlineAssemblySourceByNodeId: nestedManifest.inlineAssemblySourceByNodeId ?? {},
            },
            lineage,
          })
          if (nestedAssembly) {
            // 嵌套 loadAssemblyModel 正常路径会把资产 stage 到同一事务；但若它在顶层
            // cache 检查命中（并发期间共享 cache 已被其他 request commit 填入），则
            // 直接走 compose 分支返回，不会再次 stage。此时必须 fallback 到共享
            // cache peek，才能把子引用正确挂到父 assembly 的 assemblyAssetsByUuid。
            const nestedCacheKeys = resolveReferencedCacheKeys(partUuid, partFile)
            const stagedNested = nestedCacheKeys
              .map((cacheKey) => (
                options.transaction.peekStagedAssembly(cacheKey, options.version)
                ?? options.assemblyModelCache.peek(
                  cacheKey,
                  options.version,
                  options.transaction.publishScopeKey,
                )
              ))
              .find((asset) => Boolean(asset))
            if (stagedNested) {
              result.assemblyAsset = stagedNested
            } else {
              result.warnings.push(`嵌套 assembly 子资产未能定位: ${partUuid}`)
            }
            result.warnings.push(...nestedAssembly.warnings)
            return result
          }
        }
      }

      const partResult = parsePartAssetGltf(referencedGltf, partFile, options.version)
      try {
        await hydrateImportedPartAssetBrepRuntime(sessionId, partFile, partResult.asset, {
          projectFiles: options.projectFiles,
          importedBrepRuntimeCache: options.importedBrepRuntimeCache,
          brepRegistrar: options.brepRegistrar,
          readFileBytes: options.readFileBytes,
          guard: options.guard,
        })
        throwIfImportedModelRequestStale(options.guard)
      } catch (error) {
        disposePartAsset(partResult.asset)
        if (isImportedModelLoadAbortedError(error)) {
          throw error
        }
        // 该 catch 的作用域只覆盖 hydration 与其 stale guard；普通异常也必须稳定标记为完整性失败，
        // 防止外层 external-reference 策略把 Worker/sidecar 故障降级成“缺失子节点”。
        throw toImportedBrepHydrationError(error)
      }
      result.warnings.push(...partResult.warnings)
      // 语义 publish 统一走 transaction staging；不直写 partAssetCache。
      // transaction.stagePartAsset 为同步 push 语义，并行调用无 race。
      options.transaction.stagePartAsset(partResult.asset)
      // Level1 优化：零件资产已缓存，原始 GLTF（含 parser 和 scene 节点）不再需要，立即释放。
      // 这是 eager ownership transfer——把 geometry 所有权立即从 parsedGlbCache 交给 staged
      // PartAsset。同步 untrack 让 transaction 的 tracked set 保持与现实一致（不再重复 delete）。
      options.parsedGlbCache.delete(partFile.uuid, options.version)
      options.transaction.untrackParsedArtifact(partFile.uuid, options.version)
      result.partAsset = partResult.asset
      return result
    } catch (error) {
      // abort/stale 或 BRep hydration 完整性失败都代表整次请求不可提交；只有外部引用自身的
      // 下载/解析失败才降级为缺失节点，避免把无真实 Shape 的子资产伪装成可编辑成功状态。
      if (isImportedModelLoadAbortedError(error)
        || isImportedBrepHydrationError(error)
        || options.transaction.isAborted) {
        reportedOutcome = 'aborted'
        throw error
      }

      reportedOutcome = 'missing'
      result.missing = true
      result.warnings.push(
        `外部引用加载失败: ${partUuid}${effectivePartFileIdHint ? ` (${effectivePartFileIdHint})` : ''}`,
      )
      if (partFile) {
        // 外部引用已经下载/解析后才失败时，立即撤销本次解析缓存的所有权，避免后续命中半成品。
        options.parsedGlbCache.delete(partFile.uuid, options.version)
        options.transaction.untrackParsedArtifact(partFile.uuid, options.version)
      }
      logger.warn('[assembly-loader] 外部引用加载失败，继续加载父装配', {
        parentFileId: file.fileId,
        externalFileUuid: partUuid,
        externalFileId: effectivePartFileIdHint ?? partFile?.fileId,
        error,
      })
      return result
    } finally {
      // 最终校正：事务已 aborted 时（例如其它并行任务抛错触发 caller 的 abort，
      // 或协作抢占的 transaction.abort），以事务状态为权威，避免 "事务已死但仍报 staged"
      // 的宿主侧错误决策。missing 分支在上面已单独标过，不会被此处覆盖。
      if (options.transaction.isAborted) {
        reportedOutcome = 'aborted'
      }
      options.onChildResolveComplete?.(callbackUuid, reportedOutcome)
    }
  }

  // 并行解析所有引用子项。Fix B 的 in-flight dedup 保证兄弟节点指向同一 uuid 时只触发
  // 一次下载；事务 staging / parsedGlbCache 操作均为同步 + 幂等，在并发下安全。
  // 外部引用自身失败会以 missing 结果回流；只有请求取消/事务失效等全局错误才继续向上 reject。
  const resolutions = await mapWithConcurrencyLimit(
    manifestResult.manifest.referencedPartUuids,
    MAX_REFERENCED_CHILD_RESOLVE_CONCURRENCY,
    resolveReferencedChild,
  )

  // 合并阶段：按原 referencedPartUuids 顺序串行收尾，保留 warnings / missingPartUuids 的
  // 确定性顺序；partAssetsByUuid / assemblyAssetsByUuid 的 key 互斥，直接挂载即可。
  for (const resolution of resolutions) {
    warnings.push(...resolution.warnings)
    if (resolution.missing) {
      missingPartUuids.push(resolution.partUuid)
      continue
    }
    if (resolution.skip) {
      continue
    }
    if (resolution.assemblyAsset) {
      assemblyAssetsByUuid[resolution.partUuid] = resolution.assemblyAsset
    }
    if (resolution.partAsset) {
      partAssetsByUuid[resolution.partUuid] = resolution.partAsset
    }
  }

  // 为无 external_file_uuid 但含内嵌几何的节点生成内联 PartAsset
  for (const [nodeId, sourceObj] of Object.entries(inlinePartSourceByNodeId ?? {})) {
    const node = manifestResult.manifest.nodesById[nodeId]
    if (!node?.partFileUuid) {
      continue
    }
    // 若已有同 uuid 的缓存，跳过
    if (partAssetsByUuid[node.partFileUuid]) {
      continue
    }
    const cachedInline = options.partAssetCache.get(
      node.partFileUuid,
      options.version,
      options.transaction.publishScopeKey,
    )
    if (cachedInline) {
      partAssetsByUuid[node.partFileUuid] = cachedInline
      continue
    }
    const inlineAsset = parseInlinePartAsset(
      sourceObj,
      nodeId,
      node.name,
      file.uuid,
      options.version,
      manifestResult.manifest.unitContext,
    )
    try {
      await hydrateImportedPartAssetBrepRuntime(sessionId, file, inlineAsset, {
        projectFiles: options.projectFiles,
        importedBrepRuntimeCache: options.importedBrepRuntimeCache,
        brepRegistrar: options.brepRegistrar,
        readFileBytes: options.readFileBytes,
        guard: options.guard,
      })
      throwIfImportedModelRequestStale(options.guard)
    } catch (error) {
      disposePartAsset(inlineAsset)
      throw error
    }
    options.transaction.stagePartAsset(inlineAsset)
    partAssetsByUuid[node.partFileUuid] = inlineAsset
  }

  for (const [nodeId, sourceObj] of Object.entries(inlineAssemblySourceByNodeId ?? {})) {
    const node = manifestResult.manifest.nodesById[nodeId]
    if (!node?.partFileUuid) {
      continue
    }
    if (assemblyAssetsByUuid[node.partFileUuid]) {
      continue
    }
    const cachedInlineAssembly = options.assemblyModelCache.get(
      node.partFileUuid,
      options.version,
      options.transaction.publishScopeKey,
    )
    if (cachedInlineAssembly) {
      assemblyAssetsByUuid[node.partFileUuid] = cachedInlineAssembly
      continue
    }
    const inlineAssemblyAsset = createInlineAssemblyModelAsset(
      sourceObj,
      nodeId,
      node.name,
      file.uuid,
      options.version,
      manifestResult.manifest.unitContext,
    )
    if (!inlineAssemblyAsset) {
      continue
    }
    try {
      await hydrateImportedAssemblyAssetBrepRuntime(sessionId, file, inlineAssemblyAsset, {
        projectFiles: options.projectFiles,
        importedBrepRuntimeCache: options.importedBrepRuntimeCache,
        brepRegistrar: options.brepRegistrar,
        readFileBytes: options.readFileBytes,
        guard: options.guard,
      })
      throwIfImportedModelRequestStale(options.guard)
    } catch (error) {
      disposeAssemblyModelAssetResources(inlineAssemblyAsset)
      throw error
    }
    options.transaction.stageAssemblyAsset(inlineAssemblyAsset)
    assemblyAssetsByUuid[node.partFileUuid] = inlineAssemblyAsset
  }

  return {
    manifest: manifestResult.manifest,
    partAssetsByUuid,
    assemblyAssetsByUuid,
    missingPartUuids,
    warnings,
  }
}

export async function loadAssemblyModel(
  sessionId: string,
  file: FileInfo,
  options: LoadAssemblyModelOptions,
): Promise<ComposedAssemblyResult | null> {
  const cachedAsset = options.assemblyModelCache.get(
    file.uuid,
    options.version,
    options.transaction.publishScopeKey,
  )
  if (cachedAsset) {
    throwIfImportedModelRequestStale(options.guard)
    // 对 referenced 类型：通过模块级 composeAssemblyModel（可被测试 mock）重新合成结果
    // 对 embedded 类型：仍使用 createCachedAssemblyResult（内部调用 composeEmbeddedAssemblyModel）
    if (cachedAsset.sourceType === 'referenced') {
      return composeAssemblyModel(
        cachedAsset.manifest,
        cachedAsset.partAssetsByUuid,
      cachedAsset.assemblyAssetsByUuid,
      file,
      [...cachedAsset.warnings],
      `${cachedAsset.fileUuid}:${cachedAsset.version}`,
      false,
      cachedAsset,
    )
  }
    return createCachedAssemblyResult(cachedAsset, file)
  }

  // 错误语义（2026-04 fail-fast 修复）：
  // - abort-error：由上游 stale guard 触发，caller 已在协作抢占路径处理，直接冒泡即可；
  // - 子引用下载 / 解析 / 其它异常：一律视为 fatal，向上 reject，让 transaction
  //   在 caller 侧 abort()（或在 loadImportedModel 包装层 implicit abort），避免
  //   父装配提交部分子项后又被 scene 挂载，造成“只渲染到一条条肋骨状 mesh”的半成品。
  // - “文件确实缺失”的语义仍然通过 `result.missing = true` 在 resolveReferencedChild
  //   内表达，不走 throw 路径；只有网络 / 传输 / 解析类错误才会真正 throw 到这里。
  const resolved = await loadResolvedAssemblyAssets(sessionId, file, options)
  if (!resolved) {
    return null
  }

  // 判断是否有外部引用零件/装配或内联资产
  const hasExternalParts = resolved.manifest.referencedPartUuids.length > 0
  const hasInlineParts = Object.keys(resolved.partAssetsByUuid).some(
    (uuid) => uuid.startsWith('inline:'),
  )
  const hasInlineAssemblies = Object.keys(resolved.assemblyAssetsByUuid).some(
    (uuid) => uuid.startsWith('inline-assembly:'),
  )
  if (!hasExternalParts && !hasInlineParts && !hasInlineAssemblies) {
    const sourceGltf = options.sourceGltf
    if (!sourceGltf) {
      logger.warn(`[assembly-loader] 缺少装配模板，无法缓存完整装配体: ${file.fileId}`)
      return null
    }
    // 解析 embedded 装配文件中 meshes[].step.entities 并写入各节点 userData，
    // 使后续 collectGlbHierarchy 能正确读取实体信息
    const embeddedBodyEntries = applyGltfBodyEntriesToScene(sourceGltf)
    const embeddedAsset = createEmbeddedAssemblyModelAsset(
      sourceGltf.scene,
      resolved.manifest,
      file,
      options.version,
      resolved.warnings,
      embeddedBodyEntries,
    )
    try {
      await hydrateImportedAssemblyAssetBrepRuntime(sessionId, file, embeddedAsset, {
        projectFiles: options.projectFiles,
        importedBrepRuntimeCache: options.importedBrepRuntimeCache,
        brepRegistrar: options.brepRegistrar,
        readFileBytes: options.readFileBytes,
        guard: options.guard,
      })
      throwIfImportedModelRequestStale(options.guard)
    } catch (error) {
      disposeAssemblyModelAssetResources(embeddedAsset)
      throw error
    }
    options.transaction.stageAssemblyAsset(embeddedAsset)
    // sceneTemplate 已克隆入 embeddedAsset，parser JSON 不再需要，立即释放（33k+ accessor 描述符）
    releaseParserJson(sourceGltf)
    return createCachedAssemblyResult(embeddedAsset, file)
  }

  // 引用型装配若在当前项目会话中出现“引用子文件全部缺失”的情况：
  // 只要源 GLB 自身仍带有内嵌几何，就允许回退到 embedded 路径，避免因为引用缺口直接整模失效。
  const allReferencedPartsMissing =
    resolved.missingPartUuids.length > 0
    && resolved.missingPartUuids.length === resolved.manifest.referencedPartUuids.length
    && Object.keys(resolved.partAssetsByUuid).length === 0
    && Object.keys(resolved.assemblyAssetsByUuid).length === 0
  if (allReferencedPartsMissing && options.sourceGltf) {
    let hasMesh = false
    options.sourceGltf.scene.traverse((child) => { if (child instanceof THREE.Mesh) hasMesh = true })
    if (hasMesh) {
      logger.info(`[assembly-loader] 子文件全部缺失但检测到内嵌几何，回退 embedded 路径: ${file.fileId}`)
      const embeddedBodyEntries = applyGltfBodyEntriesToScene(options.sourceGltf)
      const embeddedAsset = createEmbeddedAssemblyModelAsset(
        options.sourceGltf.scene,
        resolved.manifest,
        file,
        options.version,
        resolved.warnings,
        embeddedBodyEntries,
      )
      try {
        await hydrateImportedAssemblyAssetBrepRuntime(sessionId, file, embeddedAsset, {
          projectFiles: options.projectFiles,
          importedBrepRuntimeCache: options.importedBrepRuntimeCache,
          brepRegistrar: options.brepRegistrar,
          readFileBytes: options.readFileBytes,
          guard: options.guard,
        })
        throwIfImportedModelRequestStale(options.guard)
      } catch (error) {
        disposeAssemblyModelAssetResources(embeddedAsset)
        throw error
      }
      options.transaction.stageAssemblyAsset(embeddedAsset)
      releaseParserJson(options.sourceGltf)
      return createCachedAssemblyResult(embeddedAsset, file)
    }
  }

  const referencedAsset = createReferencedAssemblyModelAsset(
    resolved.manifest,
    resolved.partAssetsByUuid,
    resolved.assemblyAssetsByUuid,
    file,
    options.version,
    resolved.warnings,
    options.sourceGltf?.scene,
  )
  try {
    await hydrateImportedAssemblyAssetBrepRuntime(sessionId, file, referencedAsset, {
      projectFiles: options.projectFiles,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      brepRegistrar: options.brepRegistrar,
      readFileBytes: options.readFileBytes,
      guard: options.guard,
    })
    throwIfImportedModelRequestStale(options.guard)
  } catch (error) {
    disposeAssemblyModelAssetResources(referencedAsset)
    throw error
  }
  options.transaction.stageAssemblyAsset(referencedAsset)
  // 通过模块级 composeAssemblyModel（可被测试 mock）生成结果
  const referencedResult = composeAssemblyModel(
    referencedAsset.manifest,
    referencedAsset.partAssetsByUuid,
    referencedAsset.assemblyAssetsByUuid,
    file,
    [...referencedAsset.warnings],
    `${referencedAsset.fileUuid}:${referencedAsset.version}`,
    false,
    referencedAsset,
  )
  return referencedResult
}
